import { Injectable } from "@angular/core";
import {
  ProcessedStats,
  TopBeer,
  DayOfWeekCheckinCount,
  MonthlyCheckinCount,
  RatingOverTime,
} from "../../core/models/stats.model";
import { BeerCheckin } from "src/app/core/models/beer.model";
import { DateUtils } from "../../core/utils/date-utils";

@Injectable({ providedIn: "root" })
export class StatsService {
  private memoizedStats: {
    beers: BeerCheckin[];
    start: number;
    end: number;
    result: ProcessedStats;
  } | null = null;

  public getBeerCountInRange(b: BeerCheckin, start: Date, end: Date): number {
    if (!b) return 0;
    const recentDate = b.recent_created_at
      ? DateUtils.parseDate(b.recent_created_at)
      : null;
    const firstDate = b.first_created_at
      ? DateUtils.parseDate(b.first_created_at)
      : null;

    const isRecentInRange = recentDate
      ? recentDate >= start && recentDate <= end
      : false;
    const isFirstInRange = firstDate
      ? firstDate >= start && firstDate <= end
      : false;

    if (!isRecentInRange && !isFirstInRange) {
      return 0;
    }

    const totalCount = b.count ?? 1;

    // If both first and recent check-ins fell within the range, all check-ins for this beer are in range.
    if (isFirstInRange && isRecentInRange) {
      return totalCount;
    }

    // Otherwise, only 1 check-in (e.g. recent_created_at or first_created_at) occurred in this range window.
    return 1;
  }

  computeStats(beers: BeerCheckin[], start: Date, end: Date): ProcessedStats {
    const startTime = start.getTime();
    const endTime = end.getTime();

    if (
      this.memoizedStats &&
      this.memoizedStats.beers === beers &&
      this.memoizedStats.start === startTime &&
      this.memoizedStats.end === endTime
    ) {
      return this.memoizedStats.result;
    }

    const safeBeers = beers || [];

    const beersWithCountInRange = safeBeers
      .map((b) => ({
        beer: b,
        countInRange: this.getBeerCountInRange(b, start, end),
      }))
      .filter((item) => item.countInRange > 0);

    const uniqueBeersSet = new Set(
      beersWithCountInRange
        .map((item) => item.beer.beer?.bid)
        .filter((bid): bid is number => bid !== undefined && bid !== null),
    );
    const totalUniqueBeers = uniqueBeersSet.size;

    const totalCheckins = beersWithCountInRange.reduce(
      (sum, item) => sum + item.countInRange,
      0,
    );

    const newBeersCount = safeBeers.filter((b) => {
      if (!b || !b.first_created_at || !b.beer?.bid) return false;
      const firstDate = DateUtils.parseDate(b.first_created_at);
      return (
        firstDate >= start && firstDate <= end && uniqueBeersSet.has(b.beer.bid)
      );
    }).length;

    const newBeerRatio =
      totalUniqueBeers > 0 ? newBeersCount / totalUniqueBeers : 0;

    const totalRatingSum = beersWithCountInRange.reduce(
      (sum, item) => sum + (item.beer.rating_score ?? 0) * item.countInRange,
      0,
    );

    const averageRating =
      totalCheckins > 0 ? totalRatingSum / totalCheckins : 0;

    const uniqueBreweriesSet = new Set(
      beersWithCountInRange
        .map((item) => item.beer.brewery?.brewery_name)
        .filter((name): name is string => Boolean(name)),
    );

    const totalUniqueBreweries = uniqueBreweriesSet.size;

    const beerStylesCount: Record<string, number> = {};
    const hourly: number[] = new Array(24).fill(0);
    const beerTally: Record<string, { count: number; ratingSum: number }> = {};
    const countryCounts: Record<string, number> = {};
    const stateCounts: Record<string, number> = {};
    const dailyCountsMap: Record<string, number> = {};
    const dayOfWeekCountsMap: Record<string, number> = {
      Sunday: 0,
      Monday: 0,
      Tuesday: 0,
      Wednesday: 0,
      Thursday: 0,
      Friday: 0,
      Saturday: 0,
    };
    const monthCountsMap: Record<string, number> = {
      Jan: 0,
      Feb: 0,
      Mar: 0,
      Apr: 0,
      May: 0,
      Jun: 0,
      Jul: 0,
      Aug: 0,
      Sep: 0,
      Oct: 0,
      Nov: 0,
      Dec: 0,
    };
    const dailyRatingsMap: Record<string, { sum: number; count: number }> = {};

    beersWithCountInRange.forEach(({ beer: b, countInRange: count }) => {
      const rating = b.rating_score ?? 0;
      const style = b.beer?.beer_style || "Unknown";

      const recentDate = b.recent_created_at
        ? DateUtils.parseDate(b.recent_created_at)
        : null;
      const checkinDate =
        recentDate && recentDate >= start && recentDate <= end
          ? recentDate
          : DateUtils.parseDate(b.first_created_at || b.recent_created_at);

      const hour = checkinDate.getHours();
      const name = b.beer?.beer_name || "Unknown Beer";

      beerStylesCount[style] = (beerStylesCount[style] || 0) + count;
      hourly[hour] += count;

      if (!beerTally[name]) beerTally[name] = { count: 0, ratingSum: 0 };
      beerTally[name].count += count;
      beerTally[name].ratingSum += rating * count;

      const country = b.brewery?.country_name || "Unknown";
      countryCounts[country] = (countryCounts[country] || 0) + count;

      const state = b.brewery?.location?.brewery_state || "Unknown";
      stateCounts[state] = (stateCounts[state] || 0) + count;

      const dayIso = DateUtils.toISODate(checkinDate);
      dailyCountsMap[dayIso] = (dailyCountsMap[dayIso] || 0) + count;

      const dayOfWeek = checkinDate.toLocaleDateString("en-US", {
        weekday: "long",
      });
      dayOfWeekCountsMap[dayOfWeek] =
        (dayOfWeekCountsMap[dayOfWeek] || 0) + count;

      const month = checkinDate.toLocaleDateString("en-US", { month: "short" });
      monthCountsMap[month] = (monthCountsMap[month] || 0) + count;

      if (!dailyRatingsMap[dayIso])
        dailyRatingsMap[dayIso] = { sum: 0, count: 0 };
      dailyRatingsMap[dayIso].sum += rating * count;
      dailyRatingsMap[dayIso].count += count;
    });

    const topBeers: TopBeer[] = Object.entries(beerTally)
      .map(([name, data]) => ({
        name,
        count: data.count,
        avgRating: data.count > 0 ? data.ratingSum / data.count : 0,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    const sortedDates = Object.keys(dailyCountsMap).sort();
    const recentActivityByDate = sortedDates.map((date) => ({
      date,
      count: dailyCountsMap[date],
    }));

    const sortCounts = (counts: Record<string, number>) =>
      Object.fromEntries(Object.entries(counts).sort((a, b) => b[1] - a[1]));

    const dayOfWeekOrder = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];

    const checkinsByDayOfWeek: DayOfWeekCheckinCount[] = dayOfWeekOrder.map(
      (day) => ({
        day,
        count: dayOfWeekCountsMap[day] || 0,
      }),
    );

    const monthOrder = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const checkinsByMonth: MonthlyCheckinCount[] = monthOrder.map((month) => ({
      month,
      count: monthCountsMap[month] || 0,
    }));

    const averageRatingsOverTime: RatingOverTime[] = Object.keys(
      dailyRatingsMap,
    )
      .sort()
      .map((date) => ({
        date,
        rating:
          dailyRatingsMap[date].count > 0
            ? dailyRatingsMap[date].sum / dailyRatingsMap[date].count
            : 0,
      }));

    const result: ProcessedStats = {
      totalUniqueBeers,
      totalCheckins,
      newBeersCount,
      newBeerRatio,
      averageRating,
      totalUniqueBreweries,
      beerStylesCount,
      topBeers,
      checkinsByHour: hourly,
      topCountries: sortCounts(countryCounts),
      topStates: sortCounts(stateCounts),
      recentActivityByDate,
      checkinsByDay: recentActivityByDate,
      checkinsByDayOfWeek,
      checkinsByMonth,
      averageRatingsOverTime,
    };

    this.memoizedStats = {
      beers,
      start: startTime,
      end: endTime,
      result,
    };

    return result;
  }
}
